import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  IconButton,
  TableCell,
  Badge,
  Tooltip,
  Chip,
  alpha,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import ArticleIcon from "@mui/icons-material/Article";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Edit, Trash2 } from "lucide-react";
import DataTable from "bundles/Internal/components/common/DataTable";
import BlogPostTableFilters from "bundles/Internal/components/blog_post/BlogPostTableFilters";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";

export const COLUMNS_CONSTANTS = [
  { key: "id", label: "Id" },
  { key: "online", label: "Online" },
  { key: "cover_picture_url", label: "Picture" },
  { key: "title", label: "Title" },
  { key: "resource_type", label: "Resource Type" },
  { key: "preferred_locale", label: "Preferred Locale" },
  { key: "category", label: "Category" },
  { key: "updated_at", label: "Updated At" },
  { key: "view_count", label: "View Count" },
  { key: "created_by", label: "Created by" },
];

export const RESOURCE_TYPE_COLOR = {
  candidate: { border: "#1976D2", bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
  recruiter: { border: "#079344", bg: "rgba(7,147,68,0.10)", text: "#079344" },
  hybrid: { border: "#F57C00", bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
};

export const LOCALE_COLOR = {
  fr: { border: "#1976D2", bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
  "fr-fr": { border: "#1976D2", bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
  nl: { border: "#F57C00", bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
  en: { border: "#6A1B9A", bg: "rgba(106,27,154,0.10)", text: "#6A1B9A" },
};

export const CATEGORY_COLOR = {
  career_advise: { border: "#1976D2", bg: "rgba(25,118,210,0.10)", text: "#1976D2" },
  interviews_and_cv: { border: "#079344", bg: "rgba(7,147,68,0.10)", text: "#079344" },
  candidate_actuality: { border: "#F57C00", bg: "rgba(245,124,0,0.10)", text: "#F57C00" },
  how_to_recruit: { border: "#6A1B9A", bg: "rgba(106,27,154,0.10)", text: "#6A1B9A" },
  human_resources: { border: "#D32F2F", bg: "rgba(211,47,47,0.10)", text: "#D32F2F" },
  recruiter_actuality: { border: "#757575", bg: "rgba(117,117,117,0.10)", text: "#757575" },
};

const pill = (label, c) => (
  <Chip
    label={String(label).replaceAll("_", " ")}
    size="small"
    sx={{
      backgroundColor: c.bg,
      color: c.text,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: "8px",
      height: 24,
    }}
  />
);

const formatValue = (value) => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const DATE_KEYS = new Set(["updated_at", "created_at"]);

function formatBlogPostCell(blogPost, key) {
  const value = blogPost?.[key];

  if (DATE_KEYS.has(key)) return formatDateTime(value);

  if (key === "online") {
    return value ? (
      <Chip
        label="Online"
        size="small"
        sx={{
          backgroundColor: "rgba(7,147,68,0.10)",
          color: "#079344",
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          fontSize: 11,
          fontWeight: 600,
          borderRadius: "8px",
          height: 24,
        }}
      />
    ) : (
      <Chip
        label="Offline"
        size="small"
        sx={{
          backgroundColor: "rgba(117,117,117,0.10)",
          color: "#757575",
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          fontSize: 11,
          fontWeight: 600,
          borderRadius: "8px",
          height: 24,
        }}
      />
    );
  }

  if (key === "cover_picture_url") {
    return value ? (
      <Avatar
        src={value}
        variant="rounded"
        sx={{
          width: 60,
          height: 40,
          objectFit: "cover",
        }}
      />
    ) : (
      <Box
        sx={{
          width: 60,
          height: 40,
          backgroundColor: "rgba(0,0,0,0.05)",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ArticleIcon sx={{ fontSize: 20, color: "rgba(0,0,0,0.3)" }} />
      </Box>
    );
  }

  if (key === "resource_type" && value) {
    return pill(value, RESOURCE_TYPE_COLOR[value] || RESOURCE_TYPE_COLOR.hybrid);
  }

  if (key === "preferred_locale" && value) {
    return pill(value, LOCALE_COLOR[value] || LOCALE_COLOR.en);
  }

  if (key === "category" && value) {
    return pill(value, CATEGORY_COLOR[value] || CATEGORY_COLOR.career_advise);
  }

  if (key === "created_by") {
    const c = blogPost?.created_by;
    if (!c) return "";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <UserAvatar user={c} size={22} />
        <Box component="span">{c.full_name || c.email || "Unknown"}</Box>
      </Box>
    );
  }

  return formatValue(value);
}

const BlogPostTable = ({
  blogPosts = [],
  onBlogPostSelect,
  onOpenNotes,
  onEdit,
  onDelete,
  onQueryChange,
  filters,
  onFiltersChange,
  page,
  totalCount,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
  selectable = false,
  selectedRows = [],
  onRowSelect,
  onExportCsv,
  onAddClick,
  addButtonLabel,
  canManage = true,
}) => {
  const columns = useMemo(() => COLUMNS_CONSTANTS, []);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuBlogPost, setMenuBlogPost] = useState(null);

  const renderCell = useCallback((blogPost, key) => formatBlogPostCell(blogPost, key), []);

  return (
    <>
      <DataTable
      title="Blog Posts"
      totalLabel={(count) => `${count.toLocaleString()} total blog posts`}
      emptyIcon={ArticleIcon}
      emptyTitle="No blog posts found"
      emptySubtitle="Try adjusting your search criteria"
      columns={columns}
      data={blogPosts}
      renderCell={renderCell}
      extraHeadColumns={["Notes"]}
      renderExtraCells={(blogPost) => {
        const notesCount = Number(blogPost?.notes_count || 0);
        return (
          <>
            <TableCell key="notes" sx={{ whiteSpace: "nowrap", py: 1.5 }}>
              <Tooltip title="View notes" arrow placement="top">
                <IconButton
                  size="small"
                  aria-label="Open notes"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenNotes?.(blogPost);
                  }}
                  disabled={!onOpenNotes}
                  sx={{
                    backgroundColor:
                      notesCount > 0
                        ? alpha("#079344", 0.1)
                        : "transparent",
                    "&:hover": {
                      backgroundColor: alpha("#079344", 0.2),
                    },
                  }}
                >
                  <Badge
                    badgeContent={notesCount}
                    color="primary"
                    invisible={notesCount === 0}
                    overlap="circular"
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor: "#079344",
                        fontWeight: 600,
                        fontSize: 10,
                      },
                    }}
                  >
                    <NoteAddOutlinedIcon
                      fontSize="small"
                      sx={{
                        color:
                          notesCount > 0 ? "#079344" : "text.secondary",
                      }}
                    />
                  </Badge>
                </IconButton>
              </Tooltip>
            </TableCell>
          </>
        );
      }}
      tailHeadColumns={[""]}
      renderTailCells={(blogPost) => (
        <TableCell key="actions" sx={{ whiteSpace: "nowrap", py: 1.5, width: 48 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
              setMenuBlogPost(blogPost);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </TableCell>
      )}
      onRowClick={onBlogPostSelect}
      onQueryChange={onQueryChange}
      searchPlaceholder="Search by title, content…"
      searchInfo="Title, content"
      searchById
      filtersComponent={
        filters && onFiltersChange ? (
          <BlogPostTableFilters filters={filters} onFiltersChange={onFiltersChange} />
        ) : null
      }
      page={page}
      totalCount={totalCount}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      loading={loading}
      selectable={selectable}
      selectedRows={selectedRows}
      onRowSelect={onRowSelect}
      onExportCsv={onExportCsv}
      onAddClick={onAddClick}
      addButtonLabel={addButtonLabel}
      addDisabled={!canManage}
      addTooltip="Coordinator or admin required"
    />
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuBlogPost(null); }}
        onClick={() => { setMenuAnchor(null); }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Tooltip title={canManage ? "" : "Coordinator or admin required"} arrow placement="left">
          <span>
            <MenuItem
              disabled={!canManage}
              onClick={() => { onEdit?.(menuBlogPost); setMenuBlogPost(null); }}
            >
              <ListItemIcon><Edit size={16} /></ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={canManage ? "" : "Coordinator or admin required"} arrow placement="left">
          <span>
            <MenuItem
              disabled={!canManage}
              onClick={() => { onDelete?.(menuBlogPost); setMenuBlogPost(null); }}
            >
              <ListItemIcon><Trash2 size={16} color="#d32f2f" /></ListItemIcon>
              <ListItemText sx={{ color: "#d32f2f" }}>Delete</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
    </>
  );
};

export default BlogPostTable;
